import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setShowQuestCompleteNotification } from '../../common/state/mission/reducer'
import {
  NOTIFICATION_ANIMATION_MS,
  NOTIFICATION_DURATION_MS,
} from './missionCompleteConstants'
import { MissionStarBurst } from './MissionStarBurst'

const QUEST_COMPLETED_IMAGE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/story-mission/quest-completed-notification.webp'

export const MissionQuestCompleteNotification: React.FC = () => {
  const show = useSelector(
    (state: RootState) => state.app.mission.showQuestCompleteNotification
  )
  const description = useSelector(
    (state: RootState) => state.app.mission.questCompleteDescription ?? ''
  )
  const dispatch = useDispatch()
  const [exiting, setExiting] = React.useState(false)
  const [entered, setEntered] = React.useState(false)
  const [burstKey, setBurstKey] = React.useState(0)
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    if (!show) {
      setEntered(false)
      setExiting(false)
      return
    }
    setEntered(false)
    const enterId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEntered(true)
        setBurstKey((k) => k + 1)
      })
    })
    hideTimerRef.current = setTimeout(() => {
      setExiting(true)
    }, NOTIFICATION_DURATION_MS)
    return () => {
      cancelAnimationFrame(enterId)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [show])

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName !== 'transform') return
      if (exiting) {
        dispatch(setShowQuestCompleteNotification(false))
        setExiting(false)
      }
    },
    [exiting, dispatch]
  )

  if (!show && !exiting) return null
  const isOffScreen = !entered || exiting
  return (
    <div
      className="fixed left-1/2 top-0 z-[200] flex -translate-x-1/2 flex-col items-center pointer-events-none"
      style={{ willChange: 'transform' }}
      aria-live="polite"
      role="status"
    >
      <div
        onTransitionEnd={handleTransitionEnd}
        className="flex flex-col items-center transition-transform duration-300 ease-out"
        style={{
          transform: isOffScreen ? 'translateY(-100%)' : 'translateY(0)',
          transitionDuration: `${NOTIFICATION_ANIMATION_MS}ms`,
        }}
      >
        <div className="relative">
          <div className="relative z-10">
            <img
              src={QUEST_COMPLETED_IMAGE}
              alt="Quest completed"
              className="h-auto max-h-[min(35vh,320px)] w-auto min-w-[33.33vw] max-w-[min(90vw,640px)] object-contain drop-shadow-lg"
            />
            <div
              className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 max-w-[85%] px-3 text-center pt-[120px]"
            >
              <span className="quest-complete-title-text block text-xl font-bold tracking-wide">
                Quest Completed!
              </span>
              {description && (
                <span className="quest-complete-description-text block text-sm font-medium">
                  {description}
                </span>
              )}
            </div>
          </div>
          <style>{`
            .quest-complete-title-text {
              background: linear-gradient(
                105deg,
                #fef3c7 0%,
                #fde68a 15%,
                #fcd34d 35%,
                #fef9c3 50%,
                #fcd34d 65%,
                #fde68a 85%,
                #fef3c7 100%
              );
              background-size: 200% 100%;
              -webkit-background-clip: text;
              background-clip: text;
              color: transparent;
              filter: drop-shadow(0 0 2px rgba(0,0,0,0.35)) drop-shadow(0 1px 2px rgba(0,0,0,0.3)) drop-shadow(0 0 12px rgba(251,191,36,0.5));
              animation: quest-complete-text-shine 2.5s linear infinite;
            }
            .quest-complete-description-text {
              color: #fde68a;
              text-shadow: 0 0 2px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.25);
            }
            @keyframes quest-complete-text-shine {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
          <MissionStarBurst burstKey={burstKey} visible={entered && !exiting} />
        </div>
      </div>
    </div>
  )
}
